/**
 * Scheme editor modal — shared by the Overlays card list and the overlay editor.
 *
 * It lives in components/ rather than inside OverlaysPage because both entry
 * points have to stay in step: the card’s scheme picker and the editor’s
 * "capture current colours" button open this same dialog.
 *
 * Modes:
 *   create        — start from the active scheme's palette (or template defaults)
 *   edit          — change the active scheme in place
 *   duplicate     — copy the active scheme under a new name
 *   from-overlay  — start from what this overlay's widgets are actually set to
 */
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { colorSchemesApi, overlaysApi, queryKeys, ColorScheme, RoleValue } from '@/lib/api';

// ─── Scheme Editor Modal ──────────────────────────────────────────────────────

type RoleDef = import('@/lib/api').RoleDefinition;
type Palette = import('@/lib/api').Palette;

/**
 * Single-role color editor. Handles both solid and gradient roles. Gradients
 * use a compact stops list — color + position % per row.
 */
function RoleColorEditor({
  role, value, onChange,
}: {
  role: RoleDef;
  value: RoleValue;
  onChange: (next: RoleValue) => void;
}) {
  const isGradient = value.type === 'gradient';

  // Stable handlers for switching mode without losing the operator's pick.
  const switchToSolid = () => {
    if (value.type === 'solid') return;
    onChange({ type: 'solid', color: value.stops[0]?.color ?? '#000000' });
  };
  const switchToGradient = () => {
    if (value.type === 'gradient') return;
    onChange({
      type: 'gradient',
      mode: 'linear',
      angle: 90,
      stops: [
        { color: value.color, position: 0 },
        { color: value.color, position: 100 },
      ],
    });
  };

  return (
    <div className="rounded-md border border-gray-200 px-3 py-2 dark:border-gray-700">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{role.label}</p>
          <p className="truncate text-[11px] text-gray-500 dark:text-gray-400">{role.description}</p>
        </div>
        {role.gradientCapable && (
          <div className="flex shrink-0 rounded border border-gray-300 text-[11px] dark:border-gray-600">
            <button type="button"
              onClick={switchToSolid}
              className={`px-2 py-0.5 ${!isGradient ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'}`}>
              Solid
            </button>
            <button type="button"
              onClick={switchToGradient}
              className={`px-2 py-0.5 ${isGradient ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'}`}>
              Gradient
            </button>
          </div>
        )}
      </div>

      {value.type === 'solid' ? (
        <div className="mt-2 flex items-center gap-2">
          <input
            type="color"
            value={normalizeForColorInput(value.color)}
            onChange={e => onChange({ type: 'solid', color: e.target.value })}
            className="h-7 w-12 cursor-pointer rounded border border-gray-300 bg-transparent dark:border-gray-600"
          />
          <input
            type="text"
            value={value.color}
            onChange={e => onChange({ type: 'solid', color: e.target.value })}
            className="flex-1 rounded border border-gray-300 bg-white px-2 py-1 font-mono text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            placeholder="#ffffff or transparent"
          />
        </div>
      ) : (
        <div className="mt-2 space-y-2">
          <div className="flex items-center gap-2 text-xs">
            <label className="text-gray-500 dark:text-gray-400">Mode</label>
            <select
              value={value.mode}
              onChange={e => onChange({ ...value, mode: e.target.value as 'linear' | 'radial' })}
              className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            >
              <option value="linear">Linear</option>
              <option value="radial">Radial</option>
            </select>
            {value.mode === 'linear' && (
              <>
                <label className="ml-2 text-gray-500 dark:text-gray-400">Angle°</label>
                <input
                  type="number"
                  value={value.angle}
                  onChange={e => onChange({ ...value, angle: parseFloat(e.target.value) || 0 })}
                  className="w-16 rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
              </>
            )}
          </div>
          <div className="space-y-1">
            {value.stops.map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <input
                  type="color"
                  value={normalizeForColorInput(s.color)}
                  onChange={e => {
                    const next = value.stops.map((st, j) =>
                      j === i ? { color: e.target.value, position: st.position } : st);
                    onChange({ ...value, stops: next });
                  }}
                  className="h-6 w-10 cursor-pointer rounded border border-gray-300 dark:border-gray-600"
                />
                <input
                  type="text"
                  value={s.color}
                  onChange={e => {
                    const next = value.stops.map((st, j) =>
                      j === i ? { color: e.target.value, position: st.position } : st);
                    onChange({ ...value, stops: next });
                  }}
                  className="flex-1 rounded border border-gray-300 bg-white px-2 py-1 font-mono text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={s.position}
                  onChange={e => {
                    const next = value.stops.map((st, j) =>
                      j === i ? { color: st.color, position: parseFloat(e.target.value) || 0 } : st);
                    onChange({ ...value, stops: next });
                  }}
                  className="w-14 rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
                <span className="text-gray-400">%</span>
                {value.stops.length > 2 && (
                  <button type="button"
                    onClick={() => onChange({ ...value, stops: value.stops.filter((_, j) => j !== i) })}
                    className="rounded p-0.5 text-gray-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20">
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
            <button type="button"
              onClick={() => onChange({
                ...value,
                stops: [...value.stops, { color: value.stops.at(-1)?.color ?? '#000000', position: 100 }],
              })}
              className="text-[11px] text-blue-600 hover:underline dark:text-blue-400">
              + Add stop
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * "transparent" and other CSS keywords aren't valid for <input type="color">.
 * Map any non-hex value to white so the picker renders; the text input keeps the keyword.
 */
function normalizeForColorInput(c: string): string {
  return /^#[0-9a-f]{6}$/i.test(c) || /^#[0-9a-f]{3}$/i.test(c) ? c : '#ffffff';
}

export type ModalMode = 'create' | 'edit' | 'duplicate' | 'from-overlay';

export function SchemeEditorModal({
  open, mode, source, facilityId, compositionId, onClose,
}: {
  open: boolean;
  mode: ModalMode;
  source: ColorScheme | null;
  facilityId: string;
  compositionId: number; // composition to set as active when creating
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [palette, setPalette] = useState<Palette>({});
  const [error, setError] = useState<string | null>(null);

  // Fetch the canonical role catalogue once — defines field order and gradient capability.
  const { data: rolesData } = useQuery({
    queryKey: queryKeys.colorSchemes.roles(),
    queryFn: () => colorSchemesApi.listRoles(),
    enabled: open,
    staleTime: Infinity,
  });
  const roles: RoleDef[] = rolesData?.roles ?? [];

  // What this overlay's widgets are currently set to. Only fetched for the
  // mode that needs it, so the other three modes are unchanged.
  const { data: snapshot, isLoading: snapshotLoading } = useQuery({
    queryKey: ['overlay-color-snapshot', compositionId],
    queryFn: () => overlaysApi.getColorSnapshot(compositionId),
    enabled: open && mode === 'from-overlay',
  });

  // Seed local state when the modal opens.
  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && source) {
      setName(source.name);
      setDescription(source.description ?? '');
      setPalette({ ...source.palette });
    } else if (mode === 'duplicate' && source) {
      setName(`${source.name} copy`);
      setDescription(source.description ?? '');
      setPalette({ ...source.palette });
    } else if (mode === 'from-overlay') {
      // Hold everything blank until the snapshot lands. Seeding from the active
      // scheme meanwhile would flash the wrong colours — and save them, if the
      // operator were quick with the Create button.
      if (!snapshot) {
        setName(''); setDescription(''); setPalette({});
      } else {
        setName(`${snapshot.overlayName} colours`);
        setDescription(`Captured from ${snapshot.overlayName}`);
        setPalette({ ...snapshot.palette });
      }
    } else {
      setName('');
      setDescription('');
      setPalette(source ? { ...source.palette } : {});
    }
    setError(null);
  }, [open, mode, source, snapshot]);

  // Fill in any role missing from the palette with the catalogue default so
  // the editor always renders every role row.
  const editablePalette: Palette = { ...palette };
  for (const r of roles) {
    if (!(r.id in editablePalette)) editablePalette[r.id] = r.default;
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const trimmedDesc = description.trim();
      if (mode === 'edit' && source) {
        const r = await colorSchemesApi.update(source.id, {
          name: name.trim(),
          palette: editablePalette,
          ...(trimmedDesc && { description: trimmedDesc }),
        });
        return r.scheme;
      }
      const r = await colorSchemesApi.create({
        facilityId,
        name: name.trim(),
        palette: editablePalette,
        ...(trimmedDesc && { description: trimmedDesc }),
      });
      return r.scheme;
    },
    onSuccess: async (saved) => {
      // For create + duplicate: also flip the composition to use the new scheme
      // so the operator immediately sees their work.
      if (mode !== 'edit') {
        try {
          await overlaysApi.setActiveScheme(compositionId, saved.id);
        } catch { /* surface via UI later if needed */ }
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.colorSchemes.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.overlays.all });
      onClose();
    },
    onError: (err: any) => setError(err?.message ?? 'Failed to save scheme'),
  });

  if (!open) return null;

  const title =
    mode === 'edit' ? `Edit "${source?.name}"` :
    mode === 'duplicate' ? `Duplicate "${source?.name}"` :
    mode === 'from-overlay' ? "New scheme from this overlay's colours" :
    'New Color Scheme';

  // Roles the catalogue defines that no widget in this overlay contributes to;
  // they keep the template default rather than being left blank.
  const capturedIds = new Set((snapshot?.captured ?? []).map(c => c.roleId));
  const uncaptured = roles.filter(r => !capturedIds.has(r.id));
  const shared = (snapshot?.captured ?? []).filter(c => c.shared);
  const labelFor = (roleId: string) => roles.find(r => r.id === roleId)?.label ?? roleId;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl dark:bg-gray-900">
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
          <button onClick={onClose} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
          {error && (
            <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
              {error}
            </div>
          )}

          {mode === 'from-overlay' && (
            snapshotLoading ? (
              <div className="flex items-center gap-2 rounded-md bg-gray-50 px-4 py-3 text-sm text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                <Loader2 className="h-4 w-4 animate-spin" /> Reading this overlay&apos;s colours…
              </div>
            ) : snapshot ? (
              <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200">
                <p>
                  Took {snapshot.captured.length} of {roles.length} colours from{' '}
                  {snapshot.widgetsWithBindings} widget{snapshot.widgetsWithBindings === 1 ? '' : 's'} in this overlay.
                  Adjust anything below before saving.
                </p>
                {uncaptured.length > 0 && (
                  <p className="mt-1.5 text-xs">
                    No widget here uses {uncaptured.map(r => r.label).join(', ')} — left at the template default.
                  </p>
                )}
                {shared.length > 0 && (
                  <div className="mt-1.5 text-xs">
                    <p>
                      A scheme holds one colour per role, and these roles are shared by widgets that
                      are currently set differently. Change the value below if the wrong one was taken.
                    </p>
                    <ul className="mt-0.5 list-disc pl-4">
                      {shared.map(c => (
                        <li key={c.roleId}>
                          {labelFor(c.roleId)} — shared by {c.widgets.join(', ')}; used{' '}
                          <span className="font-medium">{c.chosenFrom}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {snapshot.widgetsWithBindings === 0 && (
                  <p className="mt-1.5 text-xs">
                    None of this overlay&apos;s widgets are bound to a colour role yet, so there was nothing
                    to take. Every role below is the template default.
                  </p>
                )}
              </div>
            ) : null
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Name <span className="text-red-500">*</span>
            </label>
            <input autoFocus type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Holiday Red, Provincials 2026"
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Optional"
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Palette</p>
            <div className="space-y-2">
              {roles.map(role => (
                <RoleColorEditor
                  key={role.id}
                  role={role}
                  value={editablePalette[role.id] ?? role.default}
                  onChange={(next) => setPalette(p => ({ ...p, [role.id]: next }))}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <Button variant="outline" onClick={onClose} disabled={saveMutation.isPending}>Cancel</Button>
          <Button
            onClick={() => {
              if (!name.trim()) { setError('Name is required'); return; }
              saveMutation.mutate();
            }}
            disabled={saveMutation.isPending || (mode === 'from-overlay' && snapshotLoading)}>
            {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {mode === 'edit' ? 'Save' : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  );
}

